import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import StudentLayout from './components/StudentLayout';
import supportResourcesApi from '../api/supportResources';
import { listSubjects } from '../api/subjects';
import { AuthContext } from '../auth/AuthContext';
import { listAssessments } from '../api/assessments';
import { listSubmissions } from '../api/submissions';

const Resources = () => {
  const { user } = useContext(AuthContext) ?? {};
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [pastAssessments, setPastAssessments] = useState([]);
  const [assessmentSubmissions, setAssessmentSubmissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagFilter, setTagFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');

  const gradeOptions = useMemo(() => {
    const values = subjects.map((s) => s.grade).filter((g) => g != null);
    return Array.from(new Set(values)).sort((a, b) => a - b);
  }, [subjects]);

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        const [resourceData, subjectData, assessmentData, submissionData] = await Promise.all([
          supportResourcesApi.list(),
          listSubjects(),
          listAssessments({ grade: user?.grade }),
          listSubmissions({}),
        ]);
        setItems(Array.isArray(resourceData) ? resourceData : []);
        setSubjects(Array.isArray(subjectData) ? subjectData : []);
        const now = Date.now();
        const past = (assessmentData || []).filter((a) => {
          const due = a.dueDate ? new Date(a.dueDate).getTime() : null;
          const close = a.closeDate ? new Date(a.closeDate).getTime() : null;
          const isPast = (close != null && close < now) || (due != null && due < now);
          return isPast && (a.release?.released || a.release?.releasedAt);
        });
        setPastAssessments(past);
        const submissionMap = {};
        (submissionData || []).forEach((sub) => {
          submissionMap[sub.assessmentId] = sub;
        });
        setAssessmentSubmissions(submissionMap);
        if (user?.grade != null) {
          setGradeFilter(String(user.grade));
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load resources');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [user?.grade]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = {};
        if (tagFilter) params.tags = tagFilter;
        if (subjectFilter) params.subjectId = subjectFilter;
        if (gradeFilter) params.grade = gradeFilter;
        const data = await supportResourcesApi.list(params);
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load resources');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tagFilter, subjectFilter, gradeFilter]);

  return (
    <StudentLayout>
      <Box>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  Resources
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Download educator materials, assignments, and study packs shared with learners.
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card elevation={1} sx={{ borderRadius: 3 }}>
                    <CardHeader
                      title="Past assessments for revision"
                      subheader="Released assessments, memos, and marked work."
                    />
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      {pastAssessments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No past assessments released yet.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {pastAssessments.map((assessment) => {
                            const submission = assessmentSubmissions[assessment._id];
                            return (
                              <Card key={assessment._id} variant="outlined">
                                <CardContent>
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography fontWeight={700}>{assessment.title}</Typography>
                                      {assessment.type && (
                                        <Chip size="small" label={assessment.type.toUpperCase()} />
                                      )}
                                      {assessment.subjectId && (
                                        <Chip
                                          size="small"
                                          label={
                                            subjects.find((s) => s._id === assessment.subjectId)?.name || 'Subject'
                                          }
                                        />
                                      )}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                      {assessment.description || 'No description provided.'}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                      {(assessment.attachments || []).map((file) => (
                                        <Button
                                          key={file.key}
                                          startIcon={<DownloadRoundedIcon />}
                                          size="small"
                                          href={file.url}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {file.originalName}
                                        </Button>
                                      ))}
                                      {(assessment.memo || []).map((file) => (
                                        <Button
                                          key={file.key}
                                          startIcon={<DownloadRoundedIcon />}
                                          size="small"
                                          variant="outlined"
                                          href={file.url}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Memo: {file.originalName}
                                        </Button>
                                      ))}
                                      {(submission?.feedbackFiles || []).map((file) => (
                                        <Button
                                          key={file.key || file.url}
                                          startIcon={<DownloadRoundedIcon />}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          href={file.url}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Marked: {file.originalName || 'Marked work'}
                                        </Button>
                                      ))}
                                    </Stack>
                                  </Stack>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Filter by subject"
                      value={subjectFilter}
                      onChange={(e) => setSubjectFilter(e.target.value)}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {subjects.map((subject) => (
                        <MenuItem key={subject._id} value={subject._id}>
                          {subject.name} (G{subject.grade})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Filter by grade"
                      value={gradeFilter}
                      onChange={(e) => setGradeFilter(e.target.value)}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {gradeOptions.map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          Grade {grade}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Filter by tag"
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {['Maths', 'Science', 'EMS', 'Life Skills'].map((tag) => (
                        <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Grid>

                {items.map((item) => (
                  <Grid item xs={12} md={6} key={item._id}>
                    <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
                      <CardHeader
                        title={item.title}
                        subheader={item.description || 'No description'}
                        action={
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Chip label={item.audience || 'all'} size="small" />
                            {item.grade && <Chip size="small" label={`G${item.grade}`} />}
                            {item.subjectId && (
                              <Chip
                                size="small"
                                label={
                                  subjects.find((s) => s._id === item.subjectId)?.name || 'Subject'
                                }
                              />
                            )}
                            {(item.tags || []).map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Stack>
                        }
                      />
                      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                        <Stack spacing={1}>
                          {(item.files || []).map((file) => (
                            <Button
                              key={file.key}
                              startIcon={<DownloadRoundedIcon />}
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                              variant="outlined"
                            >
                              {file.originalName}
                            </Button>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {items.length === 0 && !loading && (
                  <Grid item xs={12}>
                    <Alert severity="info">No resources shared yet.</Alert>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </StudentLayout>
  );
};

export default Resources;
